import { createApi } from '@reduxjs/toolkit/query/react';

import { overrideFetchBaseQuery } from '../utils/overrideFetchBaseQuery';
import { baseQueryWithZodValidation } from '../utils/baseQueryWithZodValidation';
import { CUBE_CARD_IDS } from './cardIds';
import { BrandWiseMTOPendencySchema, MTOHomepageOneViewSchema } from './schemas';
import type { BrandWiseMTOPendencyResponse, MTOHomepageOneViewResponse } from './schemas';
import {
  InwardTrackerBrandSummarySchema,
  InwardTrackerItemSchema,
  InwardTrackerStoreBreakdownSchema,
} from './inwardTrackerSchemas';
import type {
  InwardTrackerBrandSummary,
  InwardTrackerItem,
  InwardTrackerStoreBreakdown,
} from './inwardTrackerSchemas';
import { SaleStockBrandSummary, SaleStockBrandSummarySchema } from './saleStockSchemas';
import {
  BrandMtdNetGmv,
  BrandMtdNetGmvSchema,
  StoreBrandSummary,
  StoreBrandSummarySchema,
  StyleStoreSplit,
  StyleStoreSplitSchema,
} from './storeBrandSchemas';
import {
  AiTagWeeklySummary,
  AiTagWeeklySummarySchema,
  BestsellersSizeDemand,
  BestsellersSizeDemandSchema,
  BestsellersTopStyles,
  BestsellersTopStylesSchema,
  CoyuSearches,
  CoyuSearchesSchema,
  TagDetailItems,
  TagDetailItemsSchema,
} from './bestsellersTrendsSchemas';
import {
  StockTransferBrandSummary,
  StockTransferBrandSummarySchema,
  StockTransferGpoList,
  StockTransferGpoListSchema,
} from './stockTransferSchemas';
import {
  MtoOrdersSummary,
  MtoOrdersSummarySchema,
  MtoLiveOrders,
  MtoLiveOrdersSchema,
  MtoWeeklyTrend,
  MtoWeeklyTrendSchema,
  MtoDelayHistogram,
  MtoDelayHistogramSchema,
  MtoDelayDetailOrders,
  MtoDelayDetailOrdersSchema,
  MtoLiveOrdersDetail,
  MtoLiveOrdersDetailSchema,
  MtoMerchHeadBrandContribution,
  MtoMerchHeadBrandContributionSchema,
  MtoMerchHeadIssueBreakdown,
  MtoMerchHeadIssueBreakdownSchema,
  MtoMerchOwnerWindowComparison,
  MtoMerchOwnerWindowComparisonSchema,
  MtoMerchHeadRawIssueLines,
  MtoMerchHeadRawIssueLinesSchema,
  MtoMerchHeadWeeklyLeaderboard,
  MtoMerchHeadWeeklyLeaderboardSchema,
  MtoMerchHeadWeeklyTrend,
  MtoMerchHeadWeeklyTrendSchema,
} from './mtoHealthSchemas';
import {
  JitB2B2CVendorPackingSlaByBrand,
  JitB2B2CVendorPackingSlaByBrandSchema,
  JitB2B2CVendorPackingSlaRaw,
  JitB2B2CVendorPackingSlaRawSchema,
  JitB2B2CVendorPackingSlaSummary,
  JitB2B2CVendorPackingSlaSummarySchema,
  JitB2B2CVendorPackingSlaWeekly,
  JitB2B2CVendorPackingSlaWeeklySchema,
} from './jitB2B2CSchemas';
import {
  CatalogCreation,
  CatalogCreationSchema,
  CatalogQuality,
  CatalogQualitySchema,
  CatalogStylesHealth,
  CatalogStylesHealthSchema,
} from './catalogingHealthSchemas';
import {
  StockAgeingBrandBucket,
  StockAgeingBrandBucketSchema,
  StockAgeingFacilityBucket,
  StockAgeingFacilityBucketSchema,
  StockAgeingWritedown,
  StockAgeingWritedownSchema,
} from './stockAgeingSchemas';
import {
  OnlinePerformanceDaily,
  OnlinePerformanceDailySchema,
} from './onlinePerformanceSchemas';
import {
  StockReconciliationFacility,
  StockReconciliationFacilitySchema,
} from './stockReconciliationSchemas';
import {
  MonthlyKpiCx,
  MonthlyKpiCxSchema,
  MonthlyKpiRevenue,
  MonthlyKpiRevenueSchema,
  MonthlyKpiSizeAvailability,
  MonthlyKpiSizeAvailabilitySchema,
} from './monthlyMerchKpiSchemas';
import { AuditHistory, AuditHistorySchema } from './auditHistorySchemas';
import { StylistLeaderboard, StylistLeaderboardSchema } from './stylistLeaderboardSchemas';
import type { ZodSchema } from 'zod';

/* Defensive parser — schema mismatches log a warning and pass the raw response
   through. Prevents one bad row from blanking the entire tab. */
const safeParse = <T,>(schema: ZodSchema, label: string) => (response: unknown): T => {
  const result = schema.safeParse(response);
  if (result.success) return result.data as T;
  // eslint-disable-next-line no-console
  console.warn(`[metabaseApi] ${label} schema mismatch — passing raw response through:`, result.error?.issues?.slice(0, 5));
  return response as T;
};

const metabaseTextParam = (name: string, value: string) => ({
  type: 'category',
  value,
  target: ['variable', ['template-tag', name]],
});

const metabaseDateRangeParam = (value: string) => ({
  type: 'date/range',
  value,
  target: ['dimension', ['template-tag', 'date_range']],
});

const metabaseBrandNameParams = (brands: string[] = []) => brands.length > 0
  ? [{ type: 'category', value: brands, target: ['variable', ['template-tag', 'brand_name']] }]
  : [];

const metabaseCategoryListParams = (name: string, values: string[] = []) => values.length > 0
  ? [{ type: 'category', value: values, target: ['variable', ['template-tag', name]] }]
  : [];

export const metabaseApi = createApi({
  reducerPath: 'metabaseApi',
  baseQuery: baseQueryWithZodValidation(overrideFetchBaseQuery({}), {
    passThroughChecks: true,
  }),
  endpoints: builder => ({
    brandWiseMTOPendency: builder.query<BrandWiseMTOPendencyResponse, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.brandWiseMTOPendency}/query/json`,
      }),
      transformResponse: (response: unknown) => {
        return BrandWiseMTOPendencySchema.parse(response);
      },
      keepUnusedDataFor: 600,
    }),
    mtoHomepageOneView: builder.query<
      MTOHomepageOneViewResponse,
      { cardId: number; vendorId?: number }
    >({
      query: ({ cardId, vendorId }) => ({
        method: 'POST',
        url: `/cube/mto-homepage-one-view/card/${cardId}/query/json`,
        headers: vendorId
          ? {
              'x-lk-vendorid-for-admin': String(vendorId),
            }
          : undefined,
        body: vendorId
          ? {
              ignore_cache: false,
              collection_preview: false,
              parameters: [
                {
                  type: 'number/=',
                  value: [vendorId],
                  target: ['variable', ['template-tag', 'vendor_id']],
                },
              ],
            }
          : undefined,
      }),
      transformResponse: (response: unknown) => {
        return MTOHomepageOneViewSchema.parse(response);
      },
      keepUnusedDataFor: 600,
    }),
    inwardTrackerBrandSummary: builder.query<InwardTrackerBrandSummary, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.inwardTrackerBrandSummary}/query/json`,
      }),
      transformResponse: (response: unknown) => {
        return InwardTrackerBrandSummarySchema.parse(response);
      },
      keepUnusedDataFor: 600,
    }),
    inwardTrackerItems: builder.query<InwardTrackerItem, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.inwardTrackerItems}/query/json`,
      }),
      transformResponse: (response: unknown) => {
        return InwardTrackerItemSchema.parse(response);
      },
      keepUnusedDataFor: 600,
    }),
    inwardTrackerStoreBreakdown: builder.query<InwardTrackerStoreBreakdown, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.inwardTrackerStoreBreakdown}/query/json`,
      }),
      transformResponse: (response: unknown) => {
        return InwardTrackerStoreBreakdownSchema.parse(response);
      },
      keepUnusedDataFor: 600,
    }),
    saleStockBrandSummary: builder.query<SaleStockBrandSummary, { dateStart: string; dateEnd: string }>({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.saleStockBrandSummary}/query/json`,
        body: {
          parameters: [
            { type: 'category', value: dateStart, target: ['variable', ['template-tag', 'date_start']] },
            { type: 'category', value: dateEnd,   target: ['variable', ['template-tag', 'date_end']]   },
          ],
        },
      }),
      transformResponse: safeParse<SaleStockBrandSummary>(SaleStockBrandSummarySchema, "saleStockBrandSummary"),
      keepUnusedDataFor: 600,
    }),
    storeBrandSummary: builder.query<StoreBrandSummary, { dateStart: string; dateEnd: string; brands?: string[] }>({
      query: ({ dateStart, dateEnd, brands = [] }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.storeBrandSummary}/query/json`,
        body: {
          parameters: [
            { type: 'category', value: dateStart, target: ['variable', ['template-tag', 'date_start']] },
            { type: 'category', value: dateEnd,   target: ['variable', ['template-tag', 'date_end']]   },
            ...(brands.length > 0
              ? [{ type: 'category', value: brands.join('|||'), target: ['variable', ['template-tag', 'brand_names']] }]
              : []),
          ],
        },
      }),
      transformResponse: safeParse<StoreBrandSummary>(StoreBrandSummarySchema, "storeBrandSummary"),
      keepUnusedDataFor: 600,
    }),
    brandMtdNetGmv: builder.query<BrandMtdNetGmv, { dateStart: string; dateEnd: string }>({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.brandMtdNetGmv}/query/json`,
        body: {
          parameters: [
            { type: 'category', value: dateStart, target: ['variable', ['template-tag', 'date_start']] },
            { type: 'category', value: dateEnd,   target: ['variable', ['template-tag', 'date_end']]   },
          ],
        },
      }),
      transformResponse: safeParse<BrandMtdNetGmv>(BrandMtdNetGmvSchema, "brandMtdNetGmv"),
      keepUnusedDataFor: 600,
    }),
    allStylesSoldStoreSplit: builder.query<StyleStoreSplit, { dateStart: string; dateEnd: string }>({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.allStylesSoldStoreSplit}/query/json`,
        body: {
          parameters: [
            { type: 'category', value: dateStart, target: ['variable', ['template-tag', 'date_start']] },
            { type: 'category', value: dateEnd,   target: ['variable', ['template-tag', 'date_end']]   },
          ],
        },
      }),
      transformResponse: safeParse<StyleStoreSplit>(StyleStoreSplitSchema, "allStylesSoldStoreSplit"),
      keepUnusedDataFor: 600,
    }),
    bestsellersTopStyles: builder.query<BestsellersTopStyles, { dateStart: string; dateEnd: string }>({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.bestsellersTopStyles}/query/json`,
        body: {
          parameters: [
            { type: 'category', value: dateStart, target: ['variable', ['template-tag', 'date_start']] },
            { type: 'category', value: dateEnd,   target: ['variable', ['template-tag', 'date_end']]   },
          ],
        },
      }),
      transformResponse: safeParse<BestsellersTopStyles>(BestsellersTopStylesSchema, "bestsellersTopStyles"),
      keepUnusedDataFor: 600,
    }),
    bestsellersSizeDemand: builder.query<BestsellersSizeDemand, { dateStart: string; dateEnd: string }>({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.bestsellersSizeDemand}/query/json`,
        body: {
          parameters: [
            { type: 'category', value: dateStart, target: ['variable', ['template-tag', 'date_start']] },
            { type: 'category', value: dateEnd,   target: ['variable', ['template-tag', 'date_end']]   },
          ],
        },
      }),
      transformResponse: safeParse<BestsellersSizeDemand>(BestsellersSizeDemandSchema, "bestsellersSizeDemand"),
      keepUnusedDataFor: 600,
    }),
    aiTagWeeklySummary: builder.query<AiTagWeeklySummary, { dateStart: string; dateEnd: string }>({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.aiTagWeeklySummary}/query/json`,
        body: {
          parameters: [
            { type: 'category', value: dateStart, target: ['variable', ['template-tag', 'date_start']] },
            { type: 'category', value: dateEnd,   target: ['variable', ['template-tag', 'date_end']]   },
          ],
        },
      }),
      transformResponse: safeParse<AiTagWeeklySummary>(AiTagWeeklySummarySchema, "aiTagWeeklySummary"),
      keepUnusedDataFor: 600,
    }),
    coyuSearchesTopQueries: builder.query<CoyuSearches, { dateStart: string; dateEnd: string }>({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.coyuSearchesTopQueries}/query/json`,
        body: {
          parameters: [
            { type: 'category', value: dateStart, target: ['variable', ['template-tag', 'date_start']] },
            { type: 'category', value: dateEnd,   target: ['variable', ['template-tag', 'date_end']]   },
          ],
        },
      }),
      transformResponse: safeParse<CoyuSearches>(CoyuSearchesSchema, "coyuSearchesTopQueries"),
      keepUnusedDataFor: 600,
    }),
    stockTransferBrandSummary: builder.query<StockTransferBrandSummary, { dateStart: string; dateEnd: string }>({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.stockTransferBrandSummary}/query/json`,
        body: {
          parameters: [
            { type: 'category', value: dateStart, target: ['variable', ['template-tag', 'date_start']] },
            { type: 'category', value: dateEnd,   target: ['variable', ['template-tag', 'date_end']]   },
          ],
        },
      }),
      transformResponse: safeParse<StockTransferBrandSummary>(StockTransferBrandSummarySchema, "stockTransferBrandSummary"),
      keepUnusedDataFor: 600,
    }),
    stockTransferGpoList: builder.query<StockTransferGpoList, { dateStart: string; dateEnd: string; brands?: string[]; brandManagers?: string[]; merchPortfolios?: string[]; stores?: string[] }>({
      query: ({ dateStart, dateEnd, brands = [], brandManagers = [], merchPortfolios = [], stores = [] }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.stockTransferGpoList}/query/json`,
        body: {
          parameters: [
            { type: 'category', value: dateStart, target: ['variable', ['template-tag', 'date_start']] },
            { type: 'category', value: dateEnd,   target: ['variable', ['template-tag', 'date_end']]   },
            ...(brands.length > 0
              ? [{ type: 'category', value: brands.join('|||'), target: ['variable', ['template-tag', 'brand_names']] }]
              : []),
            ...(brandManagers.length > 0
              ? [{ type: 'category', value: brandManagers.join('|||'), target: ['variable', ['template-tag', 'brand_managers']] }]
              : []),
            ...(merchPortfolios.length > 0
              ? [{ type: 'category', value: merchPortfolios.join('|||'), target: ['variable', ['template-tag', 'merch_portfolios']] }]
              : []),
            ...(stores.length > 0
              ? [{ type: 'category', value: stores.join('|||'), target: ['variable', ['template-tag', 'store_names']] }]
              : []),
          ],
        },
      }),
      transformResponse: safeParse<StockTransferGpoList>(StockTransferGpoListSchema, "stockTransferGpoList"),
      keepUnusedDataFor: 600,
    }),
    mtoOrdersSummary: builder.query<MtoOrdersSummary, { dateStart: string; dateEnd: string }>({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.mtoOrdersSummary}/query/json`,
        body: {
          parameters: [
            { type: 'category', value: dateStart, target: ['variable', ['template-tag', 'date_start']] },
            { type: 'category', value: dateEnd,   target: ['variable', ['template-tag', 'date_end']]   },
          ],
        },
      }),
      transformResponse: safeParse<MtoOrdersSummary>(MtoOrdersSummarySchema, "mtoOrdersSummary"),
      keepUnusedDataFor: 600,
    }),
    mtoLiveOrders: builder.query<MtoLiveOrders, void>({
      query: () => ({ method: 'POST', url: `/cube/card/${CUBE_CARD_IDS.mtoLiveOrders}/query/json` }),
      transformResponse: safeParse<MtoLiveOrders>(MtoLiveOrdersSchema, "mtoLiveOrders"),
      keepUnusedDataFor: 600,
    }),
    mtoWeeklyTrend: builder.query<MtoWeeklyTrend, void>({
      query: () => ({ method: 'POST', url: `/cube/card/${CUBE_CARD_IDS.mtoWeeklyTrend}/query/json` }),
      transformResponse: safeParse<MtoWeeklyTrend>(MtoWeeklyTrendSchema, "mtoWeeklyTrend"),
      keepUnusedDataFor: 600,
    }),
    mtoDelayHistogram: builder.query<MtoDelayHistogram, { dateStart: string; dateEnd: string }>({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.mtoDelayHistogram}/query/json`,
        body: {
          parameters: [
            { type: 'category', value: dateStart, target: ['variable', ['template-tag', 'date_start']] },
            { type: 'category', value: dateEnd,   target: ['variable', ['template-tag', 'date_end']]   },
          ],
        },
      }),
      transformResponse: safeParse<MtoDelayHistogram>(MtoDelayHistogramSchema, "mtoDelayHistogram"),
      keepUnusedDataFor: 600,
    }),
    mtoDelayDetailOrders: builder.query<MtoDelayDetailOrders, { dateStart: string; dateEnd: string }>({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.mtoDelayDetailOrders}/query/json`,
        body: {
          parameters: [
            { type: 'category', value: dateStart, target: ['variable', ['template-tag', 'date_start']] },
            { type: 'category', value: dateEnd,   target: ['variable', ['template-tag', 'date_end']]   },
          ],
        },
      }),
      transformResponse: safeParse<MtoDelayDetailOrders>(MtoDelayDetailOrdersSchema, "mtoDelayDetailOrders"),
      keepUnusedDataFor: 600,
    }),
    mtoLiveOrdersDetail: builder.query<MtoLiveOrdersDetail, void>({
      query: () => ({ method: 'POST', url: `/cube/card/${CUBE_CARD_IDS.mtoLiveOrdersDetail}/query/json` }),
      transformResponse: safeParse<MtoLiveOrdersDetail>(MtoLiveOrdersDetailSchema, "mtoLiveOrdersDetail"),
      keepUnusedDataFor: 600,
    }),
    mtoMerchHeadWeeklyLeaderboard: builder.query<MtoMerchHeadWeeklyLeaderboard, { dateRange: string; brands?: string[] }>({
      query: ({ dateRange, brands = [] }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.mtoMerchHeadWeeklyLeaderboard}/query/json`,
        body: {
          parameters: [
            metabaseDateRangeParam(dateRange),
            ...metabaseBrandNameParams(brands),
          ],
        },
      }),
      transformResponse: safeParse<MtoMerchHeadWeeklyLeaderboard>(MtoMerchHeadWeeklyLeaderboardSchema, "mtoMerchHeadWeeklyLeaderboard"),
      keepUnusedDataFor: 600,
    }),
    mtoMerchHeadSelectedWindowComparison: builder.query<MtoMerchOwnerWindowComparison, { dateStart: string; dateEnd: string; brands?: string[]; brandManagers?: string[]; merchPortfolios?: string[] }>({
      query: ({ dateStart, dateEnd, brands = [], brandManagers = [], merchPortfolios = [] }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.mtoMerchHeadSelectedWindowComparison}/query/json`,
        body: {
          parameters: [
            { type: 'category', value: dateStart, target: ['variable', ['template-tag', 'date_start']] },
            { type: 'category', value: dateEnd, target: ['variable', ['template-tag', 'date_end']] },
            ...metabaseBrandNameParams(brands),
            ...metabaseCategoryListParams('brand_manager', brandManagers),
            ...metabaseCategoryListParams('merch_portfolio', merchPortfolios),
          ],
        },
      }),
      transformResponse: safeParse<MtoMerchOwnerWindowComparison>(MtoMerchOwnerWindowComparisonSchema, "mtoMerchHeadSelectedWindowComparison"),
      keepUnusedDataFor: 600,
    }),
    mtoMerchPortfolioSelectedWindowComparison: builder.query<MtoMerchOwnerWindowComparison, { dateStart: string; dateEnd: string; brands?: string[]; brandManagers?: string[]; merchPortfolios?: string[] }>({
      query: ({ dateStart, dateEnd, brands = [], brandManagers = [], merchPortfolios = [] }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.mtoMerchPortfolioSelectedWindowComparison}/query/json`,
        body: {
          parameters: [
            { type: 'category', value: dateStart, target: ['variable', ['template-tag', 'date_start']] },
            { type: 'category', value: dateEnd, target: ['variable', ['template-tag', 'date_end']] },
            ...metabaseBrandNameParams(brands),
            ...metabaseCategoryListParams('brand_manager', brandManagers),
            ...metabaseCategoryListParams('merch_portfolio', merchPortfolios),
          ],
        },
      }),
      transformResponse: safeParse<MtoMerchOwnerWindowComparison>(MtoMerchOwnerWindowComparisonSchema, "mtoMerchPortfolioSelectedWindowComparison"),
      keepUnusedDataFor: 600,
    }),
    mtoMerchHeadWeeklyTrend: builder.query<MtoMerchHeadWeeklyTrend, { dateRange: string; brands?: string[] }>({
      query: ({ dateRange, brands = [] }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.mtoMerchHeadWeeklyTrend}/query/json`,
        body: {
          parameters: [
            metabaseDateRangeParam(dateRange),
            ...metabaseBrandNameParams(brands),
          ],
        },
      }),
      transformResponse: safeParse<MtoMerchHeadWeeklyTrend>(MtoMerchHeadWeeklyTrendSchema, "mtoMerchHeadWeeklyTrend"),
      keepUnusedDataFor: 600,
    }),
    mtoMerchHeadBrandContribution: builder.query<MtoMerchHeadBrandContribution, { dateRange: string; brands?: string[] }>({
      query: ({ dateRange, brands = [] }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.mtoMerchHeadBrandContribution}/query/json`,
        body: {
          parameters: [
            metabaseDateRangeParam(dateRange),
            ...metabaseBrandNameParams(brands),
          ],
        },
      }),
      transformResponse: safeParse<MtoMerchHeadBrandContribution>(MtoMerchHeadBrandContributionSchema, "mtoMerchHeadBrandContribution"),
      keepUnusedDataFor: 600,
    }),
    mtoMerchHeadIssueBreakdown: builder.query<MtoMerchHeadIssueBreakdown, { dateRange: string; brands?: string[] }>({
      query: ({ dateRange, brands = [] }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.mtoMerchHeadIssueBreakdown}/query/json`,
        body: {
          parameters: [
            metabaseDateRangeParam(dateRange),
            ...metabaseBrandNameParams(brands),
          ],
        },
      }),
      transformResponse: safeParse<MtoMerchHeadIssueBreakdown>(MtoMerchHeadIssueBreakdownSchema, "mtoMerchHeadIssueBreakdown"),
      keepUnusedDataFor: 600,
    }),
    mtoMerchHeadRawIssueLines: builder.query<MtoMerchHeadRawIssueLines, { dateRange: string; brands?: string[] }>({
      query: ({ dateRange, brands = [] }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.mtoMerchHeadRawIssueLines}/query/json`,
        body: {
          parameters: [
            metabaseDateRangeParam(dateRange),
            ...metabaseBrandNameParams(brands),
          ],
        },
      }),
      transformResponse: safeParse<MtoMerchHeadRawIssueLines>(MtoMerchHeadRawIssueLinesSchema, "mtoMerchHeadRawIssueLines"),
      keepUnusedDataFor: 600,
    }),
    jitB2B2CVendorPackingSlaSummary: builder.query<JitB2B2CVendorPackingSlaSummary, { dateRange: string; brand?: string }>({
      query: ({ dateRange, brand }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.jitB2B2CVendorPackingSlaSummary}/query/json`,
        body: {
          parameters: [
            metabaseDateRangeParam(dateRange),
            ...(brand ? [metabaseTextParam('brand_name', brand)] : []),
          ],
        },
      }),
      transformResponse: safeParse<JitB2B2CVendorPackingSlaSummary>(JitB2B2CVendorPackingSlaSummarySchema, "jitB2B2CVendorPackingSlaSummary"),
      keepUnusedDataFor: 600,
    }),
    jitB2B2CVendorPackingSlaWeekly: builder.query<JitB2B2CVendorPackingSlaWeekly, { dateRange: string; brand?: string }>({
      query: ({ dateRange, brand }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.jitB2B2CVendorPackingSlaWeekly}/query/json`,
        body: {
          parameters: [
            metabaseDateRangeParam(dateRange),
            ...(brand ? [metabaseTextParam('brand_name', brand)] : []),
          ],
        },
      }),
      transformResponse: safeParse<JitB2B2CVendorPackingSlaWeekly>(JitB2B2CVendorPackingSlaWeeklySchema, "jitB2B2CVendorPackingSlaWeekly"),
      keepUnusedDataFor: 600,
    }),
    jitB2B2CVendorPackingSlaByBrand: builder.query<JitB2B2CVendorPackingSlaByBrand, { dateRange: string; brand?: string }>({
      query: ({ dateRange, brand }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.jitB2B2CVendorPackingSlaByBrand}/query/json`,
        body: {
          parameters: [
            metabaseDateRangeParam(dateRange),
            ...(brand ? [metabaseTextParam('brand_name', brand)] : []),
          ],
        },
      }),
      transformResponse: safeParse<JitB2B2CVendorPackingSlaByBrand>(JitB2B2CVendorPackingSlaByBrandSchema, "jitB2B2CVendorPackingSlaByBrand"),
      keepUnusedDataFor: 600,
    }),
    jitB2B2CVendorPackingSlaRaw: builder.query<JitB2B2CVendorPackingSlaRaw, { dateRange: string; brand?: string }>({
      query: ({ dateRange, brand }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.jitB2B2CVendorPackingSlaRaw}/query/json`,
        body: {
          parameters: [
            metabaseDateRangeParam(dateRange),
            ...(brand ? [metabaseTextParam('brand_name', brand)] : []),
          ],
        },
      }),
      transformResponse: safeParse<JitB2B2CVendorPackingSlaRaw>(JitB2B2CVendorPackingSlaRawSchema, "jitB2B2CVendorPackingSlaRaw"),
      keepUnusedDataFor: 600,
    }),
    catalogCreationWeeklySummary: builder.query<CatalogCreation, { dateStart: string; dateEnd: string }>({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.catalogCreationWeeklySummary}/query/json`,
        body: {
          parameters: [
            { type: 'category', value: dateStart, target: ['variable', ['template-tag', 'date_start']] },
            { type: 'category', value: dateEnd,   target: ['variable', ['template-tag', 'date_end']]   },
          ],
        },
      }),
      transformResponse: safeParse<CatalogCreation>(CatalogCreationSchema, "catalogCreationWeeklySummary"),
      keepUnusedDataFor: 600,
    }),
    tagDetailItems: builder.query<TagDetailItems, { aiTag: string; dateStart: string; dateEnd: string }>({
      query: ({ aiTag, dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.tagDetailItems}/query/json`,
        body: {
          parameters: [
            { type: 'category', value: aiTag,     target: ['variable', ['template-tag', 'ai_tag']] },
            { type: 'category', value: dateStart, target: ['variable', ['template-tag', 'date_start']] },
            { type: 'category', value: dateEnd,   target: ['variable', ['template-tag', 'date_end']]   },
          ],
        },
      }),
      transformResponse: safeParse<TagDetailItems>(TagDetailItemsSchema, "tagDetailItems"),
      keepUnusedDataFor: 600,
    }),
    catalogQualityBrandSummary: builder.query<CatalogQuality, { dateStart: string; dateEnd: string }>({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.catalogQualityBrandSummary}/query/json`,
        body: {
          parameters: [
            { type: 'category', value: dateStart, target: ['variable', ['template-tag', 'date_start']] },
            { type: 'category', value: dateEnd,   target: ['variable', ['template-tag', 'date_end']]   },
          ],
        },
      }),
      transformResponse: safeParse<CatalogQuality>(CatalogQualitySchema, "catalogQualityBrandSummary"),
      keepUnusedDataFor: 600,
    }),
    catalogStylesHealth: builder.query<CatalogStylesHealth, void>({
      query: () => ({ method: 'POST', url: `/cube/card/${CUBE_CARD_IDS.catalogStylesHealth}/query/json` }),
      transformResponse: safeParse<CatalogStylesHealth>(CatalogStylesHealthSchema, "catalogStylesHealth"),
      keepUnusedDataFor: 600,
    }),
    stockAgeingBrandBucket: builder.query<StockAgeingBrandBucket, void>({
      query: () => ({ method: 'POST', url: `/cube/card/${CUBE_CARD_IDS.stockAgeingBrandBucket}/query/json` }),
      transformResponse: safeParse<StockAgeingBrandBucket>(StockAgeingBrandBucketSchema, "stockAgeingBrandBucket"),
      keepUnusedDataFor: 600,
    }),
    stockAgeingWritedown: builder.query<StockAgeingWritedown, void>({
      query: () => ({ method: 'POST', url: `/cube/card/${CUBE_CARD_IDS.stockAgeingWritedown}/query/json` }),
      transformResponse: safeParse<StockAgeingWritedown>(StockAgeingWritedownSchema, "stockAgeingWritedown"),
      keepUnusedDataFor: 600,
    }),
    stockAgeingFacilityBucket: builder.query<StockAgeingFacilityBucket, void>({
      query: () => ({ method: 'POST', url: `/cube/card/${CUBE_CARD_IDS.stockAgeingFacilityBucket}/query/json` }),
      transformResponse: safeParse<StockAgeingFacilityBucket>(StockAgeingFacilityBucketSchema, "stockAgeingFacilityBucket"),
      keepUnusedDataFor: 600,
    }),
    onlinePerformanceDaily: builder.query<OnlinePerformanceDaily, { dateStart: string; dateEnd: string }>({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.onlinePerformanceDaily}/query/json`,
        body: {
          parameters: [
            { type: 'category', value: dateStart, target: ['variable', ['template-tag', 'date_start']] },
            { type: 'category', value: dateEnd,   target: ['variable', ['template-tag', 'date_end']]   },
          ],
        },
      }),
      transformResponse: safeParse<OnlinePerformanceDaily>(OnlinePerformanceDailySchema, "onlinePerformanceDaily"),
      keepUnusedDataFor: 600,
    }),
    stockReconciliationFacilitySummary: builder.query<StockReconciliationFacility, void>({
      query: () => ({ method: 'POST', url: `/cube/card/${CUBE_CARD_IDS.stockReconciliationFacilitySummary}/query/json` }),
      transformResponse: safeParse<StockReconciliationFacility>(StockReconciliationFacilitySchema, "stockReconciliationFacilitySummary"),
      keepUnusedDataFor: 600,
    }),
    monthlyMerchKpiRevenue: builder.query<MonthlyKpiRevenue, void>({
      query: () => ({ method: 'POST', url: `/cube/card/${CUBE_CARD_IDS.monthlyMerchKpiRevenue}/query/json` }),
      transformResponse: safeParse<MonthlyKpiRevenue>(MonthlyKpiRevenueSchema, "monthlyMerchKpiRevenue"),
      keepUnusedDataFor: 600,
    }),
    monthlyMerchKpiSizeAvailability: builder.query<MonthlyKpiSizeAvailability, void>({
      query: () => ({ method: 'POST', url: `/cube/card/${CUBE_CARD_IDS.monthlyMerchKpiSizeAvailability}/query/json` }),
      transformResponse: safeParse<MonthlyKpiSizeAvailability>(MonthlyKpiSizeAvailabilitySchema, "monthlyMerchKpiSizeAvailability"),
      keepUnusedDataFor: 600,
    }),
    monthlyMerchKpiCx: builder.query<MonthlyKpiCx, void>({
      query: () => ({ method: 'POST', url: `/cube/card/${CUBE_CARD_IDS.monthlyMerchKpiCx}/query/json` }),
      transformResponse: safeParse<MonthlyKpiCx>(MonthlyKpiCxSchema, "monthlyMerchKpiCx"),
      keepUnusedDataFor: 600,
    }),
    stylistLeaderboard: builder.query<StylistLeaderboard, void>({
      query: () => ({ method: 'POST', url: `/cube/card/${CUBE_CARD_IDS.stylistLeaderboard}/query/json` }),
      transformResponse: safeParse<StylistLeaderboard>(StylistLeaderboardSchema, 'stylistLeaderboard'),
      keepUnusedDataFor: 600,
    }),
    auditHistory: builder.query<AuditHistory, { entityKind: string; entityId: string }>({
      query: ({ entityKind, entityId }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.auditHistory}/query/json`,
        body: {
          parameters: [
            { type: 'category', value: entityKind, target: ['variable', ['template-tag', 'entity_kind']] },
            { type: 'category', value: entityId,   target: ['variable', ['template-tag', 'entity_id']]   },
          ],
        },
      }),
      transformResponse: safeParse<AuditHistory>(AuditHistorySchema, "auditHistory"),
      keepUnusedDataFor: 60,
    }),
  }),
});

export const {
  useBrandWiseMTOPendencyQuery,
  useMtoHomepageOneViewQuery,
  useInwardTrackerBrandSummaryQuery,
  useLazyInwardTrackerItemsQuery,
  useLazyInwardTrackerStoreBreakdownQuery,
  useSaleStockBrandSummaryQuery,
  useStoreBrandSummaryQuery,
  useBrandMtdNetGmvQuery,
  useAllStylesSoldStoreSplitQuery,
  useBestsellersTopStylesQuery,
  useBestsellersSizeDemandQuery,
  useLazyBestsellersSizeDemandQuery,
  useAiTagWeeklySummaryQuery,
  useCoyuSearchesTopQueriesQuery,
  useStockTransferBrandSummaryQuery,
  useStockTransferGpoListQuery,
  useLazyStockTransferGpoListQuery,
  useMtoOrdersSummaryQuery,
  useMtoLiveOrdersQuery,
  useMtoWeeklyTrendQuery,
  useMtoDelayHistogramQuery,
  useLazyMtoDelayDetailOrdersQuery,
  useLazyMtoLiveOrdersDetailQuery,
  useMtoMerchHeadWeeklyLeaderboardQuery,
  useMtoMerchHeadSelectedWindowComparisonQuery,
  useMtoMerchPortfolioSelectedWindowComparisonQuery,
  useMtoMerchHeadWeeklyTrendQuery,
  useMtoMerchHeadBrandContributionQuery,
  useMtoMerchHeadIssueBreakdownQuery,
  useLazyMtoMerchHeadRawIssueLinesQuery,
  useJitB2B2CVendorPackingSlaSummaryQuery,
  useJitB2B2CVendorPackingSlaWeeklyQuery,
  useJitB2B2CVendorPackingSlaByBrandQuery,
  useJitB2B2CVendorPackingSlaRawQuery,
  useLazyJitB2B2CVendorPackingSlaRawQuery,
  useCatalogCreationWeeklySummaryQuery,
  useCatalogQualityBrandSummaryQuery,
  useCatalogStylesHealthQuery,
  useLazyTagDetailItemsQuery,
  useStockAgeingBrandBucketQuery,
  useLazyStockAgeingWritedownQuery,
  useStockAgeingFacilityBucketQuery,
  useOnlinePerformanceDailyQuery,
  useStockReconciliationFacilitySummaryQuery,
  useMonthlyMerchKpiRevenueQuery,
  useMonthlyMerchKpiSizeAvailabilityQuery,
  useMonthlyMerchKpiCxQuery,
  useLazyAuditHistoryQuery,
  useStylistLeaderboardQuery,
} = metabaseApi;
